import { buildChatPublicShareUrl } from "@frontend-shared/utils";
import { ExternalLink, Globe, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useChatPublicShare } from "@/api/chat/useChatPublicShare";
import { useCreateChatPublicShare } from "@/api/chat/useCreateChatPublicShare";
import { useRevokeChatPublicShare } from "@/api/chat/useRevokeChatPublicShare";
import { CopyableSecretField } from "@/components/CopyableSecretField";
import { Button } from "@/components/ui/button";
import { useShareLinkBase } from "@/hooks/use-share-link-base";
import { cn } from "@/lib/utils";

import { COPY_FEEDBACK_DURATION_MS } from "./constants";

export interface IPublicShareSectionProps {
  chatUuid: string;
  // NOTE: Public sharing is owner-only (enforced server-side). The flag hides
  // the section for anyone else opening the dialog, so the control is not shown
  // as available only to fail on submit.
  canManage: boolean;
}

type TCopyTarget = "link" | "password";

const PublicShareSectionComponent = ({
  chatUuid,
  canManage,
}: IPublicShareSectionProps): JSX.Element | null => {
  const { t } = useTranslation("chat");
  const [copied, setCopied] = useState<TCopyTarget | null>(null);

  const { data: publicShare, isLoading } = useChatPublicShare({
    chatUuid,
    enabled: canManage,
  });
  const createShare = useCreateChatPublicShare(chatUuid);
  const revokeShare = useRevokeChatPublicShare(chatUuid);
  const shareLinkBase = useShareLinkBase();

  // NOTE: Built on the same base as the authenticated share link rather than
  // taken from the server's shareUrl: the browser's own origin, so the link
  // stays correct behind a custom domain or an on-prem reverse proxy, or the
  // phone-link base the server publishes when it routes links through the
  // mobile link redirector (NSAI-5890).
  const shareUrl = publicShare
    ? buildChatPublicShareUrl({
        origin: shareLinkBase,
        shareToken: publicShare.shareToken,
      })
    : null;
  // NOTE: The mutations write their result into the share cache, so the query is
  // the single source of truth for both link and password — there is no local
  // copy of a freshly minted password that could win over a rotated-away one.
  const password = publicShare?.password ?? null;

  const handleCopy = useCallback(
    (target: TCopyTarget, value: string) => {
      void navigator.clipboard.writeText(value).then(() => {
        setCopied(target);
        toast.success(
          t(
            target === "link"
              ? "share.public_share.copied_link_toast"
              : "share.public_share.copied_password_toast",
          ),
        );
        globalThis.setTimeout(() => setCopied(null), COPY_FEEDBACK_DURATION_MS);
      });
    },
    [t],
  );

  const handleCopyLink = useCallback(
    (value: string) => handleCopy("link", value),
    [handleCopy],
  );

  const handleCopyPassword = useCallback(
    (value: string) => handleCopy("password", value),
    [handleCopy],
  );

  const handleCreate = useCallback(() => {
    // NOTE: One mutation covers both cases because only the server can tell them
    // apart; the toast is chosen from what the client already knew was on screen.
    const isRegenerating = Boolean(publicShare);

    createShare.mutate(
      {},
      {
        onSuccess: () => {
          toast.success(
            t(
              isRegenerating
                ? "share.public_share.regenerated_toast"
                : "share.public_share.created_toast",
            ),
          );
        },
        onError: () => toast.error(t("share.public_share.create_error")),
      },
    );
  }, [createShare, publicShare, t]);

  const handleRevoke = useCallback(() => {
    revokeShare.mutate(undefined, {
      onSuccess: () => toast.success(t("share.public_share.revoked_toast")),
      onError: () => toast.error(t("share.public_share.revoke_error")),
    });
  }, [revokeShare, t]);

  if (!canManage) {
    return null;
  }

  const isMutating = createShare.isPending || revokeShare.isPending;

  return (
    <div
      className="mt-2 rounded-lg border border-border p-4"
      data-testid="chat-public-share-section"
    >
      <div className="flex items-start gap-2">
        <Globe className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-foreground">
            {t("share.public_share.title")}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("share.public_share.subtitle")}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t("share.public_share.loading")}
        </div>
      ) : shareUrl && publicShare ? (
        <div className="mt-3 space-y-3">
          <div>
            <CopyableSecretField
              label={t("share.public_share.link_label")}
              value={shareUrl}
              copyLabel={t("share.public_share.copy_link")}
              onCopy={handleCopyLink}
              isCopied={copied === "link"}
              testId="chat-public-share-link-input"
              copyTestId="chat-public-share-copy-link"
            />
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="chat-public-share-go-to-link"
              className="mt-2 inline-flex items-center text-xs font-medium text-primary hover:underline"
            >
              <ExternalLink className="me-1.5 h-3.5 w-3.5" />
              {t("share.public_share.go_to_link")}
            </a>
          </div>

          {password ? (
            <CopyableSecretField
              label={t("share.public_share.password_label")}
              value={password}
              copyLabel={t("share.public_share.copy_password")}
              onCopy={handleCopyPassword}
              isCopied={copied === "password"}
              isMaskable
              isMonospace
              showLabel={t("share.public_share.show_password")}
              hideLabel={t("share.public_share.hide_password")}
              testId="chat-public-share-password-input"
              copyTestId="chat-public-share-copy-password"
              toggleTestId="chat-public-share-toggle-password"
            />
          ) : (
            <p
              className="text-xs text-muted-foreground"
              data-testid="chat-public-share-password-unavailable"
            >
              {t("share.public_share.password_unavailable_note")}
            </p>
          )}

          {/* NOTE: The two properties a person sharing a conversation most needs
              to know before they hand the link over — that it is frozen, and
              that attachments do not travel with it. Stated on the owner's side
              rather than only on the viewer's, where it would be too late. */}
          <div className="space-y-1 text-xs text-muted-foreground">
            <p data-testid="chat-public-share-snapshot-note">
              {t("share.public_share.snapshot_note")}
            </p>
            <p>{t("share.public_share.text_only_note")}</p>
            <p data-testid="chat-public-share-message-count">
              {t("share.public_share.message_count", {
                count: publicShare.sharedMessageCount,
              })}
            </p>
            <p>{t("share.public_share.never_expires")}</p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isMutating}
              data-testid="chat-public-share-regenerate"
              onClick={handleCreate}
            >
              <RefreshCw
                className={cn(
                  "me-2 h-3.5 w-3.5",
                  createShare.isPending && "animate-spin",
                )}
              />
              {t("share.public_share.regenerate")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isMutating}
              data-testid="chat-public-share-revoke"
              onClick={handleRevoke}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="me-2 h-3.5 w-3.5" />
              {t("share.public_share.revoke")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          disabled={isMutating}
          data-testid="chat-public-share-create"
          onClick={handleCreate}
        >
          {createShare.isPending ? (
            <Loader2 className="me-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Globe className="me-2 h-3.5 w-3.5" />
          )}
          {t("share.public_share.create")}
        </Button>
      )}
    </div>
  );
};

export const PublicShareSection = memo(PublicShareSectionComponent);
